import { AttachMoney } from '@mui/icons-material';
import { Grid, TextField, Typography } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { OrderDto, QuoteDto, SiteHaulingLineItemDto } from '../../../../../dtos';
import {
    useArchiveLineItemByIdMutation,
    useArchiveSiteHaulingLineItemDeleteFromDatabaseByIdMutation,
    useGetLineItemDoesHaulingLineItemHaveCorrespondingMaterialLineItemsQuery,
    useGetLineItemHasBeenOrderedQuery,
    usePatchLineItemByIdMutation
} from '../../../../../store/generated/generatedApi';
import { LineItemTypes } from '../../../../../util';
import { EquipmentTypeSelect, SiteSelect, UnitOfMeasureSelect, PoAndJobNumberInput } from '../../../../CommonInputs';
import { SlimFormHeader } from '../../../../Core/SlimFormHeader';
import { FormInput, FormSection, IFormProps, LoadingIndicator } from '../../../../CoreLib/library';
import { RateInputForm } from '../RateInputForm';
import { useSiteHaulingLineItemForm } from './useSiteHaulingLineItemForm';

export interface ISiteHaulingLineItemFormProps extends IFormProps<SiteHaulingLineItemDto> {
    quote?: QuoteDto;
    order?: OrderDto;
}

export const SiteHaulingLineItemForm: FC<ISiteHaulingLineItemFormProps> = (props) => {
    const { isLoading, quote, order } = props;
    const {
        isFormDirty,
        handleSave,
        handleCancel,
        fieldErrors,
        handleQuoteChange,
        handleSiteChange,
        handleEquipmentTypeChange,
        handleRateChange,
        handleDriverRateChange,
        handleBrokerRateChange,
        handleUnitOfMeasureChange,
        handleMinimumFlatRateChange,
        handleDriverMinimumFlatRateChange,
        handleBrokerMinimumFlatRateChange,
        handleRateToggleChange,
        handleZoneChange,
        handleDriverMemoChange,
        handleCustomerJobNumberChange,
        handlePoNumberChange,
        handleIsCustomPoNumberChange,
        handleIsCustomerJobNumberCustomChange,
        formQuote,
        formSite,
        formEquipmentType,
        formHaulingRate,
        formDriverRate,
        formBrokerRate,
        formUnitOfMeasure,
        formIsActive,
        formMinimumFlatRate,
        formDriverMinimumFlatRate,
        formBrokerMinimumFlatRate,
        formRateToggle,
        formZone,
        formDriverMemo,
        formCustomerJobNumber,
        formPoNumber,
        formIsCustomPoNumber,
        formIsCustomerJobNumberCustom,
        formQuotePoNumber,
        formQuoteCustomerJobNumber,
        validateField
    } = useSiteHaulingLineItemForm(props, quote);

    const {
        data: canDeleteData,
        error: canDeleteError,
        isLoading: canDeleteLoading,
    } = useGetLineItemDoesHaulingLineItemHaveCorrespondingMaterialLineItemsQuery({ id: props.initValues?.id ?? '', lineItemType: LineItemTypes.SiteHauling });
    const { data: hasBeenOrdered, error: hasBeenOrderedError, isLoading: hasBeenOrderedLoading } = useGetLineItemHasBeenOrderedQuery({ id: props.initValues?.id ?? '' });

    const [canDelete, setCanDelete] = useState(true);
    useEffect(() => {
        if (canDeleteError) {
            if ((canDeleteError as any).status === 404) {
                setCanDelete(true);
            }
        }
        if (canDeleteData && canDeleteData === true) {
            setCanDelete(false);
        }
    }, [canDeleteData, canDeleteError]);

    useEffect(() => {
        if (hasBeenOrderedError) {
            if ((hasBeenOrderedError as any).status === 404) {
                setCanDelete(true);
            }
        }
        if (hasBeenOrdered && hasBeenOrdered === true) {
            setCanDelete(false);
        }
    }, [hasBeenOrdered, hasBeenOrderedError]);

    useEffect(() => {
        if (quote && !formQuote) {
            handleQuoteChange(quote);
        }
    }, [formQuote, handleQuoteChange, quote]);

    const currentSiteHaulingLineItemId = useMemo(() => {
        return props.initValues?.id;
    }, [props.initValues?.id]);

    const rateInputs = useCallback(() => {
        return (
            <RateInputForm
                haulingRate={formHaulingRate}
                setHaulingRate={handleRateChange}
                haulingRateFieldName='haulingRate'
                minimumFlatRate={formMinimumFlatRate}
                setMinimumFlatRate={handleMinimumFlatRateChange}
                minimumFlatRateFieldName='haulingFlatRate'
                driverRate={formDriverRate}
                setDriverRate={handleDriverRateChange}
                driverRateFieldName='driverRate'
                driverFlatRate={formDriverMinimumFlatRate}
                setDriverFlatRate={handleDriverMinimumFlatRateChange}
                driverFlatRateFieldName='driverFlatRate'
                brokerRate={formBrokerRate}
                setBrokerRate={handleBrokerRateChange}
                brokerRateFieldName='brokerRate'
                brokerFlatRate={formBrokerMinimumFlatRate}
                setBrokerFlatRate={handleBrokerMinimumFlatRateChange}
                brokerFlatRateFieldName='brokerFlatRate'
                rateToggle={formRateToggle}
                setRateToggle={handleRateToggleChange}
                fieldErrors={fieldErrors}
            />
        );
    }, [
        fieldErrors,
        formDriverMinimumFlatRate,
        formDriverRate,
        formHaulingRate,
        formMinimumFlatRate,
        formRateToggle,
        formBrokerMinimumFlatRate,
        formBrokerRate,
        handleDriverMinimumFlatRateChange,
        handleDriverRateChange,
        handleMinimumFlatRateChange,
        handleRateChange,
        handleRateToggleChange,
        handleBrokerMinimumFlatRateChange,
        handleBrokerRateChange,
    ]);

    if (isLoading || canDeleteLoading || hasBeenOrderedLoading) {
        return <LoadingIndicator />;
    }

    return (
        <Grid container direction='column'>
            <SlimFormHeader
                objectName='Haul Rate Line Item'
                permissionsTypeName={'lineItem'}
                icon={<AttachMoney />}
                title={formSite ? `Hauling to site ${formSite.code}` : 'New Haul Rate Line Item'}
                breadcrumbs={
                    order
                        ? [
                            { label: 'Home', navLink: '/' },
                            { label: 'Jobs', navLink: '/jobs' },
                            { label: `Job #${quote?.quoteNumber}`, navLink: `/job/${order?.jobId}` },
                            { label: `Order #${order?.orderNumber}`, navLink: `/job/${order?.jobId}/order/${order?.id}` },
                        ]
                        : [
                            { label: 'Home', navLink: '/' },
                            { label: 'Quotes', navLink: '/quotes' },
                            { label: `${quote?.jobName ? `${quote.jobName} - ` : ''}${quote?.customer?.customerNumber ? `${quote.customer.customerNumber}: ` : ''}${quote?.customer?.name ?? ''} (Quote #${quote?.quoteNumber ?? 'Quote'})`, navLink: `/quote/${quote?.id}` },
                            { label: `Line Items`, navLink: `/quote/${quote?.id}/lineItems` },
                        ]
                }
                isActive={formIsActive}
                id={currentSiteHaulingLineItemId}
                isFormDirty={isFormDirty}
                handleCancel={handleCancel}
                handleSave={handleSave}
                entityNameSingular={'haul rate line item'}
                canDelete={canDelete}
                canSave={true}
                deleteMutation={useArchiveSiteHaulingLineItemDeleteFromDatabaseByIdMutation}
                archiveRestoreProps={{
                    restoreMutation: usePatchLineItemByIdMutation,
                    archiveMutation: useArchiveLineItemByIdMutation,
                    isActive: formIsActive,
                }}
            />
            <Grid item mx={2}>
                <FormSection>
                    <Grid item>
                        <Typography variant='h2'>General</Typography>
                    </Grid>
                    <Grid item container direction='row' spacing={3}>
                        <Grid item xs={12} sm={6} md={3} xl={2}>
                            <EquipmentTypeSelect
                                selectedEquipmentTypeId={formEquipmentType?.id ?? ''}
                                handleSelectedEquipmentTypeChange={handleEquipmentTypeChange}
                                required
                                errorMessage={fieldErrors.get('equipmentTypeId')}
                                disabled={!canDelete}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3} xl={2}>
                            <UnitOfMeasureSelect
                                selectedUnit={formUnitOfMeasure ?? ''}
                                handleSelectedUnitChange={handleUnitOfMeasureChange}
                                required
                                errorMessage={fieldErrors.get('unitOfMeasure')}
                                onBlur={() => validateField('unitOfMeasure')}
                                disabled={!canDelete}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3} xl={4}>
                            <SiteSelect
                                selectedSiteId={formSite?.id ?? ''}
                                handleSelectedSiteChange={handleSiteChange}
                                required
                                errorMessage={fieldErrors.get('siteId')}
                                disabled={!canDelete}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3} xl={4}>
                            <FormInput value={formZone} onChange={(event) => handleZoneChange(event.target.value)} label='Zone' fullWidth />
                        </Grid>
                        <Grid item xs={12}>
                            <PoAndJobNumberInput
                                formQuotePoNumber={formQuotePoNumber}
                                formIsCustomPoNumber={formIsCustomPoNumber}
                                handleIsCustomPoNumberChange={handleIsCustomPoNumberChange}
                                formPoNumber={formPoNumber}
                                formQuoteCustomerJobNumber={formQuoteCustomerJobNumber}
                                formIsCustomerJobNumberCustom={formIsCustomerJobNumberCustom}
                                formCustomerJobNumber={formCustomerJobNumber}
                                handleIsCustomerJobNumberCustomChange={handleIsCustomerJobNumberCustomChange}
                                handleCustomerJobNumberChange={handleCustomerJobNumberChange}
                                handlePoNumberChange={handlePoNumberChange}
                                fieldErrors={fieldErrors}
                            />
                        </Grid>
                    </Grid>
                    <Grid item>
                        <Grid item xs={12} sm={12} md={12}>
                            <TextField
                                value={formDriverMemo ?? ''}
                                onChange={handleDriverMemoChange}
                                label='Order Memo'
                                fullWidth
                                multiline
                                minRows={3}
                            />
                        </Grid>
                    </Grid>
                    <Grid item>
                        <Typography variant='h2'>Rates</Typography>
                    </Grid>
                    <Grid item container direction='row' spacing={3}>
                        <Grid item xs={12} md={6}>
                            {rateInputs()}
                        </Grid>
                    </Grid>
                </FormSection>
            </Grid>
        </Grid>
    );
};
